import { useMemo, useState } from 'react'
import { StatusPill } from '@/components/administration/kpi-metrics/shared/status-pill'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { PersonTimelineVereAgir } from '@/types'
import { formatDateToDisplay } from '@/utils/formatDate'
import { TimelineEmptyState } from '../person-timeline-modal'
import { groupByKindAndRisk } from './group-by-kind-and-risk'
import { KindRiskTable } from './kind-risk-table'

const SITUATION_LABEL: Record<string, string> = {
	STOP_AND_FIX: 'Parar e corrigir',
	FIX_AND_CONTINUE: 'Corrigir e continuar',
}

export function VereAgirTimelineSection({
	data,
}: {
	data: PersonTimelineVereAgir
}) {
	const [mode, setMode] = useState<'created' | 'asResponsible'>('created')

	const kindRiskGroups = useMemo(
		() => groupByKindAndRisk(data.created.items),
		[data.created.items],
	)

	return (
		<div className="flex flex-col gap-4">
			<Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
				<TabsList className="w-fit">
					<TabsTrigger value="created">
						Criados por ele ({data.created.total})
					</TabsTrigger>
					<TabsTrigger value="asResponsible">
						Como responsável ({data.asResponsible.total})
					</TabsTrigger>
				</TabsList>
			</Tabs>

			{mode === 'created' ? (
				<>
					<KindRiskTable title="Por tipo e risco" groups={kindRiskGroups} />

					{data.created.items.length === 0 ? (
						<TimelineEmptyState label="Nenhum Ver e Agir criado no período." />
					) : (
						<div className="rounded-lg border border-kaefer-light-silver">
							<Table className="table-fixed">
								<TableHeader>
									<TableRow className="bg-muted/30">
										<TableHead className="w-24">Data</TableHead>
										<TableHead className="w-28">Tipo</TableHead>
										<TableHead className="w-28">Risco</TableHead>
										<TableHead className="w-28">Status</TableHead>
										<TableHead className="w-32">Situação</TableHead>
										<TableHead>Descrição</TableHead>
										<TableHead>Ação imediata</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.created.items.map((item) => (
										<TableRow key={item.id}>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDateToDisplay(item.date)}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{item.kindName}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{item.riskName}
											</TableCell>
											<TableCell>
												<StatusPill status={item.status} />
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{SITUATION_LABEL[item.situation] ?? item.situation}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word text-muted-foreground">
												{item.description}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word text-muted-foreground">
												{item.immediateAction ?? '—'}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</>
			) : (
				<>
					<p className="text-xs text-muted-foreground">
						{data.asResponsible.resolved} resolvido(s) ·{' '}
						{data.asResponsible.pending} pendente(s)
					</p>
					{data.asResponsible.items.length === 0 ? (
						<TimelineEmptyState label="Nenhuma pendência como responsável no período." />
					) : (
						<div className="rounded-lg border border-kaefer-light-silver">
							<Table className="table-fixed">
								<TableHeader>
									<TableRow className="bg-muted/30">
										<TableHead className="w-24">Data</TableHead>
										<TableHead>Tipo</TableHead>
										<TableHead>Risco</TableHead>
										<TableHead className="w-28">Status</TableHead>
										<TableHead className="w-24">Prazo</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.asResponsible.items.map((item) => (
										<TableRow
											key={item.id}
											className={cn(
												item.status !== 'RESOLVED' && 'bg-kaefer-red/5',
											)}
										>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDateToDisplay(item.date)}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{item.kindName}
											</TableCell>
											<TableCell className="whitespace-normal wrap-break-word">
												{item.riskName}
											</TableCell>
											<TableCell>
												<StatusPill status={item.status} />
											</TableCell>
											<TableCell className="whitespace-nowrap text-muted-foreground">
												{formatDateToDisplay(item.dueDate)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</>
			)}
		</div>
	)
}
